import { cn } from "@/lib/utils"

function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "animate-pulse rounded-md bg-gradient-to-r from-muted via-muted/50 to-muted bg-[length:200%_100%] animate-[shimmer_2s_infinite]",
        className
      )}
      {...props}
    />
  )
}

// Preset skeleton components
function SkeletonCard() {
  return (
    <div className="rounded-lg border p-4 space-y-3 animate-fade-in">
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-8 w-full" />
    </div>
  )
}

function SkeletonWallet() {
  return (
    <div className="rounded-lg border p-4 space-y-4 animate-fade-in">
      <div className="flex items-center space-x-4">
        <Skeleton className="h-12 w-12 rounded-full" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-1/4" />
        </div>
      </div>
      <Skeleton className="h-8 w-full" />
    </div>
  )
}

function SkeletonTransaction() {
  return (
    <div className="flex items-center space-x-4 p-4 border-b animate-fade-in">
      <Skeleton className="h-10 w-10 rounded-full" />
      <div className="space-y-2 flex-1">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-3 w-1/3" />
      </div>
      <Skeleton className="h-4 w-16" />
    </div>
  )
}

// Mobile-specific skeletons
function SkeletonDashboard() {
  return (
    <div className="space-y-4 px-4 pt-2 animate-fade-in">
      {/* Banner skeleton */}
      <Skeleton className="h-32 w-full rounded-2xl" />
      
      {/* Portfolio summary skeleton */}
      <div className="rounded-2xl border p-4 space-y-3">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-8 w-40" />
        <div className="flex gap-4 pt-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
      
      {/* Wallets section skeleton */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-8 w-24 rounded-lg" />
        </div>
        {[1, 2].map((i) => (
          <div key={i} className="rounded-2xl border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="h-12 w-12 rounded-xl" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
              <div className="text-right space-y-2">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-5 w-12 rounded-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Quick actions skeleton */}
      <div className="rounded-2xl border p-4 space-y-4">
        <Skeleton className="h-5 w-28" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-14 rounded-xl" />
          <Skeleton className="h-14 rounded-xl" />
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-16 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  )
}

function SkeletonNotification() {
  return (
    <div className="space-y-3 animate-fade-in">
      {[1, 2, 3, 4].map((i) => (
        <div 
          key={i} 
          className="flex items-start gap-3 p-4 rounded-2xl border"
          style={{ animationDelay: `${i * 100}ms` }}
        >
          <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-12" />
            </div>
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        </div>
      ))}
    </div>
  )
}

function SkeletonWalletCard() {
  return (
    <div className="rounded-2xl border p-4 animate-fade-in">
      <div className="flex items-center justify-between min-h-[44px]">
        <div className="flex items-center gap-3">
          <Skeleton className="h-12 w-12 rounded-xl" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
        <div className="text-right space-y-2">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-12 rounded-full" />
        </div>
      </div>
    </div>
  )
}

export { 
  Skeleton, 
  SkeletonCard, 
  SkeletonWallet, 
  SkeletonTransaction,
  SkeletonDashboard,
  SkeletonNotification,
  SkeletonWalletCard
}
